import { ReportsRepo } from '@services/reports.repo';
import { UsersRepo } from '@services/users.repo';
import { ReportingService } from '@common/reporting.service';
import * as _ from 'lodash'
import * as moment from 'moment'
import { EmailStatus } from '@models/portfolioadmin/reportManagement/emailStatus';
import { AuthService } from '@common/auth.service';
import { Roles } from '@common/enums/Roles'
import { extractErrorMessage } from '@common/utils/errorUtils';

class PortfolioManagementReportController implements ng.IOnInit {
  public clientId: number;
  public selectedPortfolioIds: any
  public selectedCurrency: any
  public clientPortfolios: any
  public clientNumber: any

  public isLoading: Boolean = false;

  public reportTypes: any;
  public reports: any = {};

  /** @ngInject */
  constructor(private $scope: angular.IScope, private notifyjs: any, private $mdDialog: angular.material.IDialogService,
    private $rootScope: angular.IRootScopeService, private reportsRepo: ReportsRepo, private usersRepo: UsersRepo,
    private reportingService: ReportingService, private authService: AuthService) { }

  $onInit() {
    this.getClientReports();
  }


  refreshReports = () => {
    this.getClientReports();
  }

  generateReport = () => {
    const tempScope = this.$rootScope.$new(true) as any;
    tempScope.selectedPortfolios = this.selectedPortfolioIds;
    tempScope.selectedCurrency = this.selectedCurrency;
    tempScope.clientNumber = this.clientNumber;
    tempScope.clientPortfolios = this.clientPortfolios.map((portfolio) => portfolio.id)
    tempScope.lockedDates = _.map(_.flatMap(this.reports, (r) => _.filter(r, t => t.isLocked)) as any[], r => r.date)
    this.$mdDialog.show({
      template: `<generate-report-dialog selected-portfolios="selectedPortfolios" selected-currency="selectedCurrency" restricted-dates="lockedDates" client-number="clientNumber" client-portfolios="clientPortfolios"></generate-report-dialog>`,
      clickOutsideToClose: true,
      scope: tempScope
    })
      .then((result => {
        if (result) {
          // Silently refresh data in the background
          this.getClientReportsSilently();
        }
      }))
  }


  getClientReports = () => {
    this.getReportTypes()
      .then(() => {
        _.forEach(this.reportTypes, (reportType) => {
          // at first just fetch the reports for the first client
          this.getReportsForReportType(this.clientNumber, reportType);
        });

      });

  }

  private getReportTypes() {
    this.isLoading = true;
    return this.reportsRepo.getReportTypes()
      .then((res) => {
        if (res && res.data) {
          let array = res.data as Array<any>;
          let filteredArray = array.filter(item => item.name !== "PortfolioAttribution");
          this.reportTypes = _.map(filteredArray, (reportType) => { return reportType.name });
        }
      })
      .catch(() => {
        this.notifyjs("The reports could not be loaded. Please try again later or contact the System Administrator.", "error");
      })
      .finally(() => this.isLoading = false);
  }

  private getReportsForReportType(clientNumber, reportType) {
    this.isLoading = true;
    return this.fetchAndProcessReports(clientNumber, reportType, true)
      .finally(() => this.isLoading = false);
  }

  private fetchAndProcessReports(clientNumber, reportType, showErrors: boolean = false) {
    return this.reportsRepo.getClientReportByType(clientNumber, reportType)
      .then((reportDetails) => {
        this.reports[reportType] = [];

        _.forEach(reportDetails, (report) => {
          const displayDate = moment(report.reportDate).format('MMMM Y');
          let currentReport = _.find(this.reports[reportType], (r) => r['date'] == displayDate);

          if (!currentReport) {
            currentReport = {
              date: displayDate,
              type: reportType,
              isLocked: report.isLocked,
              isReconciled: report.isReconciled,
              reports: [],
              custodianReports : report.custodianReports,
              sendRecipients: report.sendRecipients
            }
            this.reports[reportType].push(currentReport);
          }

          const reportDetailsObj = {reportId: report.reportId,
            bucket: report.bucketName,
            reportName: '',
            key: report.reportKey,
            emailStatuses: report.emailStatuses,
            isLocked: report.isLocked,
          };

          if (report.externalSource === null) {
            reportDetailsObj.reportName = `Confident (${report.reportCurrency})`;
          } else if (report.externalSource === 'MCB') {
            reportDetailsObj.reportName = 'MCB';
          } else if (report.externalSource === 'Rothschild') {
            reportDetailsObj.reportName = 'EDR';
          }

          currentReport.reports.push(reportDetailsObj);
          currentReport.isLocked = _.some(currentReport.reports, (r: any) => r.isLocked);
        });
      })
      .catch((err) => {
        if (showErrors) {
          this.notifyjs("The reports could not be loaded. Please try again later or contact the System Administrator.", "error");
        } else {
          console.error("Error refreshing reports silently:", err);
        }
      });
  }

  public getDownloadCountOutput(report: any) {
    let keys = Object.keys(report.reports);
    let count = keys.length;
    if (count === 1)
      return "1 report";
    else
      return `${count} reports`;
  }

  public formatToStartCase(word) {
    return _.startCase(word);
  }

  public openReport(reportDetails: any) {
    this.reportingService.openReport(reportDetails.key);
  }

  public isSending(reportDetails): boolean {
    if (!reportDetails) {
      return true;
    }

    return reportDetails.emailStatuses.some(s => s.status === EmailStatus.SENDING)
  }

  public toggleReportLock(reportDetails, clientReport: any) {
    const newLockState = !clientReport.isLocked;

    const userRoles = this.authService.getUserRoles();
    if (!newLockState && (userRoles.indexOf(Roles.SeniorManagement) === -1 && userRoles.indexOf(Roles.SystemAdministrator) === -1)) {
      this.notifyjs('Only senior management can unlock a report.', 'warning');
      return;
    }

    const previousLockState = clientReport.isLocked;
    clientReport.isLocked = newLockState;
    // reportDetails.isLocked = _.some(reportDetails.reports, (r: any) => r.isLocked);

    this.reportsRepo.toggleReportLock([clientReport.reportId], newLockState)
      .then(() => {
        // Success - state is already updated
      })
      .catch((err) => {
        clientReport.isLocked = previousLockState;
        // reportDetails.isLocked = _.some(reportDetails.reports, (r: any) => r.isLocked);
        const errorMessage = err.data || err.message || 'Failed to toggle report lock. Please try again.';
        this.notifyjs(errorMessage, 'error');
      });
  }

  private displayStatus(status: EmailStatus) {
    switch (status) {
      case EmailStatus.PENDING:
        return 'Not Sent';
      case EmailStatus.SENDING:
        return 'Busy';
      case EmailStatus.OPENED:
        return 'Opened';
      case EmailStatus.FAILED:
        return 'Failed To Send';
      case EmailStatus.SENT:
      default:
        return 'Sent';
    }
  }
  public emailStatusTooltip(reportDetails: any): string {
    if (!reportDetails || !reportDetails.emailStatuses || reportDetails.emailStatuses.length === 0) {
      return 'Not sent to anyone';
    }
    return reportDetails.emailStatuses
      .map((s: any) => `${s.emailAddress} - ${this.displayStatus(s.status)}`)
      .join('\n');
  }

  public emailReport(reportDetails: any) {
    if (reportDetails.emailStatuses.every(s => s.status === EmailStatus.PENDING)) {
      this.reportsRepo.sendReportEmails(reportDetails.reportId)
        .then(() => reportDetails.emailStatuses.forEach(s => s.status = EmailStatus.SENDING))
        .catch(err => this.notifyjs(extractErrorMessage(err)));
    } else if (reportDetails.emailStatuses.some(s => s.status === EmailStatus.SENDING)) {
      return;
    } else if (reportDetails.emailStatuses.every(s => s.status === EmailStatus.SENT || s.status === EmailStatus.FAILED || s.status === EmailStatus.OPENED)) {
      const dialogScope = this.$scope.$new() as any;
      dialogScope.emails = reportDetails.emailStatuses;

      this.$mdDialog.show({
        template: '<md-dialog><email-select-dialog available-emails="emails"></email-select-dialog></md-dialog>',
        scope: dialogScope,
      })
        .then((emailIds) =>
          this.reportsRepo.resendReportEmailToSpecificClients(reportDetails.reportId, emailIds)
            .catch(err => this.notifyjs(err))
        )
        .then(() => reportDetails.emailStatuses.forEach(s => s.status = EmailStatus.SENDING))
        .catch(err => console.log('Cancelled'));
    }
  }

  public openEmailReportsModal = (report: any) => {
    const tempScope = this.$rootScope.$new(true) as any;
    
    // Prepare generated reports
    const generatedReports = (report.reports || []).map((r: any) => ({
      reportId: r.reportId,
      reportName: this.formatReportName(r),
      reportDate: report.date,
      isCustodian: false,
      approved: true, // Generated reports are always approved
      isLocked: report.isLocked,
      emailStatuses: r.emailStatuses || [],
      selected: false,
      disabled: !report.isLocked // Only locked reports can be emailed
    }));

    // Prepare uploaded reports
    const custodianReports = (report.custodianReports || []).map((cr: any) => ({
      reportId: cr.reportId,
      reportName: this.formatCustodianReportName(cr),
      reportDate: report.date,
      isCustodian: true,
      approved: cr.approved,
      emailStatuses: cr.emailStatuses || [],
      selected: false,
      disabled: !cr.approved // Only approved uploaded reports can be emailed
    }));

    tempScope.generatedReports = generatedReports;
    tempScope.custodianReports = custodianReports;
    tempScope.reportDate = report.date;
    tempScope.sendRecipients = report.sendRecipients;
    tempScope.users = [];
    tempScope.isLoadingUsers = true;
    this.usersRepo.getUsers()
      .then((users) => {
        tempScope.users = users || [];
        tempScope.isLoadingUsers = false;
      })
      .catch(() => {
        tempScope.users = [];
        tempScope.isLoadingUsers = false;
      });
    const controller = this;
    tempScope.sendReports = (clientReportIds: number[], custodianReportIds: number[], recipientUserIds: number[], recipientCcUserIds?: number[]) => {
      if (clientReportIds.length === 0 && custodianReportIds.length === 0) {
        return Promise.reject(new Error('No report IDs provided'));
      }
      return controller.reportsRepo.sendMultipleReports(clientReportIds, custodianReportIds, recipientUserIds, recipientCcUserIds)
        .then(() => {
          const sentReports = [
            ...generatedReports.filter(r => clientReportIds.includes(r.reportId)),
            ...custodianReports.filter(r => custodianReportIds.includes(r.reportId))
          ];
          sentReports.forEach(r => {
            r.emailStatuses.forEach((status: any) => {
              status.status = EmailStatus.SENDING;
            });
          });
          controller.getClientReportsSilently();
          return true;
        });
    };

    this.$mdDialog.show({
      template: `<md-dialog flex="40">
                   <email-reports-dialog 
                     generated-reports="generatedReports" 
                     custodian-reports="custodianReports"
                     report-date="reportDate"
                     send-recipients="sendRecipients"
                     users="users"
                     is-loading-users="isLoadingUsers"
                     on-send="sendReports(clientReportIds, custodianReportIds, recipientUserIds, recipientCcUserIds)">
                   </email-reports-dialog>
                 </md-dialog>`,
      clickOutsideToClose: true,
      scope: tempScope
    })
    .then((result) => {
      if (result) {
        // Modal closed successfully after sending
        this.notifyjs("Reports are being sent successfully.", "success");
      }
    })
    .catch(() => {
      // Modal was cancelled, do nothing
    });
  }


  uploadCustodianReport = (report) => {
    const tempScope = this.$rootScope.$new(true) as any;

    tempScope.clientPortfolios = this.clientPortfolios.map((portfolio) => {
      return {
        id: portfolio.id,
        name: portfolio.name,
        accounts: portfolio.accounts || []
      }
    });

    tempScope.reportDate = report.date
    tempScope.clientNumber = this.clientNumber

    this.$mdDialog.show({
      template: `<custodian-report-dialog client-portfolios="clientPortfolios" report-date="reportDate" client-number="clientNumber"> </custodian-report-dialog>`,
      clickOutsideToClose: true,
      scope: tempScope
    })
    .then(res => {
      if(res){
        // Silently refresh data in the background
        this.getClientReportsSilently();
      }
    })

  }


  deleteCustodianReport = (custodianReport) => {
    const reportName = this.formatCustodianReportName(custodianReport);
    const tempScope = this.$rootScope.$new(true) as any;
    const controller = this;
    tempScope.message = `Are you sure you want to delete "${reportName}"? This action cannot be undone.`;
    tempScope.isLoading = false;

    tempScope.confirmDelete = () => {
      tempScope.isLoading = true;
      
      // Optimistically remove the report from the UI
      let reportRemoved = false;
      _.forEach(controller.reports, (reportArray, reportType) => {
        _.forEach(reportArray, (report) => {
          if (report.custodianReports) {
            const index = report.custodianReports.findIndex(cr => cr.reportId === custodianReport.reportId);
            if (index !== -1) {
              report.custodianReports.splice(index, 1);
              reportRemoved = true;
            }
          }
        });
      });

      // Delete with loading state
      controller.reportsRepo.deleteCustodianReport(custodianReport.reportId)
        .then(res => {
          // Silently refresh data in the background
          controller.getClientReportsSilently();
          controller.$mdDialog.hide();
        })
        .catch(err => {
          // Revert the optimistic update on error
          if (reportRemoved) {
            controller.getClientReports();
          }
          tempScope.isLoading = false;
          controller.notifyjs("Failed to delete custodian report " + extractErrorMessage(err));
        });
    };

    tempScope.cancelDelete = () => {
      controller.$mdDialog.cancel();
    };

    controller.$mdDialog.show({
      template: `<md-dialog>
                   <reusable-modal 
                     title="Confirm Delete"
                     on-accept="cancelDelete()"
                     on-cancel="confirmDelete()"
                     accept-button-text="Cancel"
                     cancel-button-text="Delete"
                     is-loading="isLoading">
                     <p>{{message}}</p>
                   </reusable-modal>
                 </md-dialog>`,
      clickOutsideToClose: true,
      scope: tempScope
    }).catch(() => {
      // Dialog was cancelled, do nothing
    });
  }

  deleteClientReport = (clientReport) => {
    const reportName = this.formatReportName(clientReport);
    const tempScope = this.$rootScope.$new(true) as any;
    const controller = this;
    tempScope.message = `Are you sure you want to delete "${reportName}"? This action cannot be undone.`;
    tempScope.isLoading = false;

    tempScope.confirmDelete = () => {
      tempScope.isLoading = true;
      
      // Optimistically remove the report from the UI
      let reportRemoved = false;
      _.forEach(controller.reports, (reportArray, reportType) => {
        _.forEach(reportArray, (report) => {
          if (report.reports) {
            const index = report.reports.findIndex(r => r.reportId === clientReport.reportId);
            if (index !== -1) {
              report.reports.splice(index, 1);
              reportRemoved = true;
              
              // If this was the last report in the group, we might want to remove the entire report group
              // But for now, we'll just leave it empty
            }
          }
        });
      });

      // Delete with loading state
      controller.reportsRepo.deleteClientReport(clientReport.reportId)
        .then(res => {
          // Silently refresh data in the background
          controller.getClientReportsSilently();
          controller.$mdDialog.hide();
        })
        .catch(err => {
          // Revert the optimistic update on error
          if (reportRemoved) {
            controller.getClientReports();
          }
          tempScope.isLoading = false;
          controller.notifyjs("Failed to delete client report " + extractErrorMessage(err));
        });
    };

    tempScope.cancelDelete = () => {
      controller.$mdDialog.cancel();
    };

    controller.$mdDialog.show({
      template: `<md-dialog>
                   <reusable-modal 
                     title="Confirm Delete"
                     on-accept="cancelDelete()"
                     on-cancel="confirmDelete()"
                     accept-button-text="Cancel"
                     cancel-button-text="Delete"
                     is-loading="isLoading">
                     <p>{{message}}</p>
                   </reusable-modal>
                 </md-dialog>`,
      clickOutsideToClose: true,
      scope: tempScope
    }).catch(() => {
      // Dialog was cancelled, do nothing
    });
  }

  private getClientReportsSilently = () => {
    // Refresh data without showing loading state
    // Only refresh if report types are already loaded
    if (this.reportTypes && this.reportTypes.length > 0) {
      _.forEach(this.reportTypes, (reportType) => {
        this.getReportsForReportTypeSilently(this.clientNumber, reportType);
      });
    }
  }

  private getReportsForReportTypeSilently(clientNumber, reportType) {
    // Fetch reports without setting isLoading
    return this.fetchAndProcessReports(clientNumber, reportType, false); // silently fetches with no errors or loader
  }

  public formatCustodianReportName(custodianReport: any): string {
    if (custodianReport.name) {
      // Use name as is
      return custodianReport.name;
    } else {
      // Fallback to the old format if there is no name
      return `${custodianReport.externalSource} ${custodianReport.accountNumber} (${custodianReport.reportCurrency}) Report`;
    }
  }

  public formatReportName(report: any): string {
    // Use reportName as is (backend already handles formatting)
    return report.reportName || '';
  }

  public canApproveCustodianReport(custodianReport: any, report: any): boolean {
    // Use the canApprove flag from backend if available
    if (custodianReport.canApprove !== undefined) {
      return custodianReport.canApprove;
    }
    
    // Fallback logic if backend flag is not available
    const userRoles = this.authService.getUserRoles();
    const hasPortfolioRole = userRoles && (
      userRoles.indexOf(Roles.PortfolioTeam) !== -1 ||
      userRoles.indexOf(Roles.SeniorManagement) !== -1 ||
      userRoles.indexOf(Roles.SystemAdministrator) !== -1
    );
    
    if (!hasPortfolioRole) {
      return false;
    }
    
    return true;
  }

  public getApprovalTooltip(custodianReport: any, report: any): string {
    const isApproved = custodianReport.approved;
    const reason = (custodianReport.cannotApproveReason || '').toLowerCase();
    
    // If there's a specific reason why approval/unapproval is not allowed, show message for the actual action
    if (custodianReport.cannotApproveReason) {
      if (reason.includes('creator')) {
        return isApproved ? "Cannot unapprove as creator" : "Cannot approve as creator";
      }
      if (reason.includes('insufficient role')) {
        return isApproved ? "Cannot unapprove: Insufficient role" : "Insufficient role";
      }
      if (isApproved) {
        return custodianReport.cannotApproveReason.replace(/approve/gi, "unapprove");
      }
      return custodianReport.cannotApproveReason;
    }
    
    if (isApproved) {
      return "Unapprove";
    } else {
      return "Approve";
    }
  }

  public approveCustodianReport = (custodianReport: any, report: any) => {
    if (!this.canApproveCustodianReport(custodianReport, report)) {
      const reason = this.getApprovalTooltip(custodianReport, report);
      this.notifyjs(reason, "error");
      return;
    }

    // Optimistically update the UI
    const previousApproved = custodianReport.approved;
    custodianReport.approved = true;

    this.reportsRepo.approveCustodianReport(custodianReport.reportId)
      .then(() => {
        // Success - refresh data to get updated approval info
        this.getClientReportsSilently();
      })
      .catch((err) => {
        // Revert the optimistic update on error
        custodianReport.approved = previousApproved;
        const errorMessage = err.data || err.message || 'Failed to approve custodian report. Please try again.';
        this.notifyjs(errorMessage, 'error');
      });
  }

  public unapproveCustodianReport = (custodianReport: any, report: any) => {
    if (!this.canApproveCustodianReport(custodianReport, report)) {
      const reason = this.getApprovalTooltip(custodianReport, report);
      this.notifyjs(reason, "error");
      return;
    }

    // Optimistically update the UI
    const previousApproved = custodianReport.approved;
    custodianReport.approved = false;

    this.reportsRepo.unapproveCustodianReport(custodianReport.reportId)
      .then(() => {
        // Success - refresh data to get updated approval info
        this.getClientReportsSilently();
      })
      .catch((err) => {
        // Revert the optimistic update on error
        custodianReport.approved = previousApproved;
        const errorMessage = err.data || err.message || 'Failed to unapprove custodian report. Please try again.';
        this.notifyjs(errorMessage, 'error');
      });
  }

}

export const portfolioManagementReport: angular.IComponentOptions = {
  template: require('./portfolioadmin.reports.html'),
  controller: PortfolioManagementReportController,
  bindings: {
    clientId: "<",
    selectedPortfolioIds: "<",
    selectedCurrency: "<",
    clientPortfolios: "<",
    clientNumber: "<"
  }
}



// WEBPACK FOOTER //
// ./src/app/portfolioadmin/reportManagement/portfolioadmin.reports.ts